import { graphHooks } from '@tunasong/graph-lib/react'
import { ThumbsUp } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Comment, Persisted } from '@tunasong/schemas'
import { isAgentAuthoredComment } from '@tunasong/schemas'
import { IconButton, Tooltip } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { useId, useState } from 'react'
import { useChatActions } from './chat-actions.hook.js'
import { useChatToast } from './chat-toast.js'
import { likeButtonName, likersText } from './like-names.js'
import styles from './like-button.module.css'

export interface LikeButtonProps {
  comment: Persisted<Comment>
}

/** Who likes it, with mine applied while a like or unlike is on its way */
const likersWith = (likedBy: string[], me: string | undefined, liked: boolean) => {
  const others = likedBy.filter(id => id !== me)
  return liked && me ? [...others, me] : others
}

/**
 * Like a comment (GH #2796; spec §3.1). A quiet ghost button under the body, always visible: pressed, it fills in the
 * primary colour and the count shows beside it. Who liked it is a tooltip on the count, and the button's description,
 * so it never depends on hover. Your own comment has no button, but the count and names show once others like it. An
 * agent's comment has neither, since no person receives the like. Liking is instant and silent; if the server refuses,
 * it goes back and the chat's toast says so, with Try again.
 */
export const LikeButton: FC<LikeButtonProps> = ({ comment }) => {
  const { userId } = useCurrentUser()
  const { like, unlike } = useChatActions()
  const showToast = useChatToast()
  const descriptionId = useId()
  const { profile } = graphHooks.useProfile(comment.userId)
  const likedBy = comment.likedBy ?? []
  /** The like or unlike just sent, shown until the server's version of the comment arrives */
  const [pending, setPending] = useState<{ from: string[] | undefined; liked: boolean } | null>(null)
  const liked =
    pending && pending.from === comment.likedBy ? pending.liked : Boolean(userId && likedBy.includes(userId))
  const likers = likersWith(likedBy, userId, liked)
  const { getName } = graphHooks.useProfiles({ userIds: likers })

  if (isAgentAuthoredComment(comment)) {
    return null
  }
  const names = likersText(likers.map(getName))
  const description = names ? (
    <span id={descriptionId} className={styles.visuallyHidden}>
      {names}
    </span>
  ) : null
  const count = likers.length > 0 ? <span className={styles.count}>{likers.length}</span> : null

  if (comment.userId === userId) {
    return count ? (
      <Tooltip title={names}>
        <span className={styles.own} aria-describedby={descriptionId}>
          <ThumbsUp className={styles.icon} aria-hidden="true" />
          {count}
          {description}
        </span>
      </Tooltip>
    ) : null
  }

  const handleClick = async (next: boolean) => {
    setPending({ from: comment.likedBy, liked: next })
    const saved = await (next ? like(comment) : unlike(comment))
    if (!saved) {
      setPending(null)
      showToast({
        message: 'Couldn’t save your like. Try again.',
        actionLabel: 'Try again',
        onAction: () => void handleClick(next),
        severity: 'error',
      })
    }
  }

  const button = (
    <IconButton
      size="small"
      className={cn(styles.button, liked && styles.pressed)}
      aria-pressed={liked}
      aria-label={likeButtonName(profile?.name ?? 'Unknown', Boolean(comment.task), likers.length)}
      aria-describedby={names ? descriptionId : undefined}
      onClick={() => void handleClick(!liked)}
    >
      <ThumbsUp className={styles.icon} aria-hidden="true" />
      {count}
    </IconButton>
  )
  return (
    <>
      {names ? <Tooltip title={names}>{button}</Tooltip> : button}
      {description}
    </>
  )
}
